import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Row, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';
import { INDUCTEE_FIELDS, CHECKLIST_SECTIONS } from '../constants/staffInductionChecklist';

export default function StaffInductionDetail() {
  const { id }   = useParams();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const isOwner  = user?.role === 'owner';
  const canEdit  = can('canManageStaff') || isOwner;

  const [induction, setInduction] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    api.get(`/staff-inductions/${id}`)
      .then(({ data }) => setInduction(data.induction))
      .catch(err => setError(err.response?.data?.message || 'Failed to load induction'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!await confirmDelete('This induction record, including both signatures, will be permanently removed.')) return;
    try { await api.delete(`/staff-inductions/${id}`); navigate('/staff-induction'); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  };

  const handleDownloadPDF = () => {
    const token   = localStorage.getItem('cs_token');
    const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
    window.open(`${baseURL}/api/staff-inductions/${id}/pdf?token=${token}`, '_blank');
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', color:'var(--muted)' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );
  if (error || !induction) return (
    <div style={{ padding:'40px', color:'var(--red)', fontFamily:'Inter,sans-serif', textAlign:'center' }}>{error || 'Induction not found'}</div>
  );

  let lastParent = null;

  return (
    <div style={T.page}>
      <PageHeader title={`Induction — ${induction.workerName}`} onBack={()=>navigate(-1)}
        actions={
          <div style={{ display:'flex', gap:'8px' }}>
            {induction.status !== 'invited' && (   // nothing to download until the worker has filled it in
              <button onClick={handleDownloadPDF} style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}>
                <i className="ti ti-file-download" aria-label="Download PDF"/>
              </button>
            )}
            {canEdit && (
              <button onClick={()=>navigate(`/staff-induction/${id}/edit`)} style={{ ...T.btnIcon, color:'var(--amber)', borderColor:'rgba(133,79,11,0.25)' }}>
                <i className="ti ti-edit" aria-label="Edit"/>
              </button>
            )}
            {isOwner && (
              <button onClick={handleDelete} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                <i className="ti ti-trash" aria-label="Delete"/>
              </button>
            )}
          </div>
        }
      />

      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'20px 16px 60px' }}>

        {induction.status === 'invited' && (
          <Alert type="warning">
            Waiting for {induction.workerName} to fill this in. Their login{induction.tempExpiresAt && new Date(induction.tempExpiresAt) > new Date()
              ? ` expires ${new Date(induction.tempExpiresAt).toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}.`
              : ' has expired — send a new one from the list.'}
          </Alert>
        )}
        {induction.status === 'awaiting_supervisor' && (
          <Alert type="info">{induction.workerName} has completed and signed this. {canEdit ? 'Use Edit to review it and add the supervisor signature.' : 'It is waiting for the supervisor to sign off.'}</Alert>
        )}

        <Section title="Inductee Details">
          {INDUCTEE_FIELDS.map(f => (
            (induction[f.key] || f.key === 'medicalNotes') && (
              <Field key={f.key} label={f.label}>
                <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color:'var(--text)' }}>
                  {f.type === 'date' && induction[f.key]
                    ? new Date(induction[f.key]).toLocaleDateString('en-AU', { day:'2-digit', month:'long', year:'numeric' })
                    : induction[f.key] || '—'}
                </div>
              </Field>
            )
          ))}
        </Section>

        {CHECKLIST_SECTIONS.map(section => {
          const showParentHeader = section.parent && section.parent !== lastParent;
          lastParent = section.parent;
          const checks = induction.checklist?.[section.key] || [];
          const filled = checks.filter(Boolean).length;
          return (
            <div key={section.key}>
              {showParentHeader && (
                <div style={{ fontSize:'12px', fontWeight:'600', color:'var(--blue)', textTransform:'uppercase', letterSpacing:'.5px', margin:'20px 0 8px' }}>
                  {section.parent}
                </div>
              )}
              <Section title={`${section.title} (${filled}/${section.items.length})`}>
                {section.items.map((label, idx) => (
                  <div key={idx} style={{ display:'flex', alignItems:'flex-start', gap:'10px', padding:'7px 0', borderBottom:'1px solid var(--bg)' }}>
                    <i className={`ti ${checks[idx] ? 'ti-circle-check' : 'ti-circle'}`} style={{ fontSize:'16px', color: checks[idx] ? 'var(--blue)' : 'var(--hint)', flexShrink:0, marginTop:'1px' }} aria-hidden="true"/>
                    <span style={{ fontSize:'13px', color:'var(--text)', lineHeight:1.5 }}>{label}</span>
                  </div>
                ))}
              </Section>
            </div>
          );
        })}

        <Section title="Sign-off Declaration">
          <Row cols={2}>
            <div>
              <div style={{ fontSize:'11px', fontWeight:'600', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'8px' }}>
                Worker — {induction.workerSignatureName}
              </div>
              {induction.workerSignature && (
                <img src={induction.workerSignature} alt="Worker signature" style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fafafa' }}/>
              )}
              <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'4px' }}>
                {induction.workerSignedDate ? new Date(induction.workerSignedDate).toLocaleDateString('en-AU') : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize:'11px', fontWeight:'600', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'8px' }}>
                Supervisor — {induction.supervisorSignatureName}
              </div>
              {induction.supervisorSignature && (
                <img src={induction.supervisorSignature} alt="Supervisor signature" style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fafafa' }}/>
              )}
              <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'4px' }}>
                {induction.supervisorSignedDate ? new Date(induction.supervisorSignedDate).toLocaleDateString('en-AU') : '—'}
              </div>
            </div>
          </Row>
        </Section>

        <div style={{ fontSize:'12px', color:'var(--muted)', textAlign:'center', marginTop:'8px' }}>
          Submitted by {induction.submittedByName || induction.submittedBy?.name || '—'} · {new Date(induction.createdAt).toLocaleDateString('en-AU', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
        </div>
      </div>
    </div>
  );
}
